import type { CheckoutLoadingPhase } from '~~/layers/base/types/checkout'

/** UX escalation thresholds (ms) — optimistic until `slow`, then tips at `engaged`, fail UI at `critical`. */
export const CHECKOUT_LOADING_THRESHOLDS_MS = {
    slow: 5_000,
    engaged: 8_000,
    critical: 30_000,
} as const

export type CheckoutLoadingThresholds = typeof CHECKOUT_LOADING_THRESHOLDS_MS

/** Derive phase from elapsed time (pure — used by controller and tests). */
export function resolveCheckoutLoadingPhase(
    elapsedMs: number,
    thresholds: CheckoutLoadingThresholds = CHECKOUT_LOADING_THRESHOLDS_MS
): CheckoutLoadingPhase {
    if (elapsedMs >= thresholds.critical) {
        return 'critical'
    }
    if (elapsedMs >= thresholds.engaged) {
        return 'engaged'
    }
    if (elapsedMs >= thresholds.slow) {
        return 'slow'
    }
    if (elapsedMs > 0) {
        return 'optimistic'
    }
    return 'idle'
}

export type CheckoutLoadingPhaseController = {
    start: () => void
    stop: () => void
}

/** Schedules phase transitions; call `stop()` when the tracked operation completes. */
export function createCheckoutLoadingPhaseController(
    onPhaseChange: (phase: CheckoutLoadingPhase) => void,
    thresholds: CheckoutLoadingThresholds = CHECKOUT_LOADING_THRESHOLDS_MS
): CheckoutLoadingPhaseController {
    let timers: ReturnType<typeof setTimeout>[] = []
    let active = false

    const stop = () => {
        timers.forEach(clearTimeout)
        timers = []
        active = false
        onPhaseChange('idle')
    }

    const start = () => {
        stop()
        active = true
        onPhaseChange('optimistic')
        timers = [
            setTimeout(() => {
                if (active) {
                    onPhaseChange('slow')
                }
            }, thresholds.slow),
            setTimeout(() => {
                if (active) {
                    onPhaseChange('engaged')
                }
            }, thresholds.engaged),
            setTimeout(() => {
                if (active) {
                    onPhaseChange('critical')
                }
            }, thresholds.critical),
        ]
    }

    return { start, stop }
}
